import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { flow } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import { connect, useSelector } from 'react-redux';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { getQuery } from 'client/utils/location';

import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { LeaseDealsModel, LeaseDealsPaths } from 'client/data/models/lease-deals';
import { VisitorModel } from 'client/data/models/visitor';

import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/lease-deals';

import { URL_CONTEXT_PATH, UrlModel } from 'client/data/models/url';
import { LeaseDealsEntities } from 'site-modules/lease-deals/constants/prop-types';
import { ZERO_DOWN_PARAM } from 'site-modules/shared/constants/lease-deals/lease-deals';

import { withLeaseDealsListTracking } from 'client/site-modules/lease-deals/components/lease-deals-list-tracking/lease-deals-list-tracking';
import { usePersistedData } from 'site-modules/lease-deals/hooks/use-persisted-data/use-persisted-data';
import { useRedirectOnStateChange } from 'site-modules/lease-deals/hooks/use-redirect-on-state-change/use-redirect-on-state-change';
import { useRestoreFiltersFromHistory } from 'site-modules/lease-deals/hooks/use-restore-filters-from-history/use-restore-filters-from-history';

import { Loading } from 'site-modules/lease-deals/components/loading/loading';
import { LeaseDealsHeader } from 'client/site-modules/lease-deals/components/lease-deals-header/lease-deals-header';
import { LeaseDealsGradientContainer } from 'client/site-modules/lease-deals/components/lease-deals-gradient-container/lease-deals-gradient-container';
import { LeaseDealsIconNav } from 'client/site-modules/lease-deals/components/lease-deals-icon-nav/lease-deals-icon-nav';
import { LeaseDealsEvaluation } from 'client/site-modules/lease-deals/components/lease-deals-evaluation/lease-deals-evaluation';
import { LeaseDealsFilters } from 'site-modules/lease-deals/components/filters/lease-deals-filters/lease-deals-filters';
import { ViewToggle } from 'site-modules/lease-deals/components/view-toggle/view-toggle';
import { LeaseDealsCardList } from 'client/site-modules/lease-deals/components/lease-deals-card-list/lease-deals-card-list';
import { LowInventoryScreen } from 'site-modules/lease-deals/components/low-inventory-screen/low-inventory-screen';
import { LeaseDealsDisclaimer } from 'client/site-modules/lease-deals/components/lease-deals-disclaimer/lease-deals-disclaimer';
import { LeaseDealsTypeContent } from 'site-modules/lease-deals/components/lease-deals-type-content/lease-deals-type-content';
import { Shop3947Experiment } from 'site-modules/shared/components/shop-3947-experiment/shop-3947-experiment';
import { LeaseDealsSortToggle } from 'client/site-modules/lease-deals/components/lease-deals-sort-toggle/lease-deals-sort-toggle';
import { TrackInvalidDealId } from 'site-modules/lease-deals/components/track-invalid-deal-id/track-invalid-deal-id';
import { DealCheckerWidget } from 'site-modules/lease-deals/components/deals-checker-widget/deal-checker-widget';
import { LeaseDealPreview } from 'site-modules/lease-deals/components/lease-deal-preview/lease-deal-preview';

import 'site-modules/lease-deals/styles/global.scss';
import './lease-deals.scss';

export function LeaseDealsUI({
  isMobile = false,
  urlContext = undefined,
  visitorLocation = {},
  leaseDealsData: leaseDealsDataProp = undefined,
  withPreviewCard = false,
  setModelValue,
}) {
  const [leaseDealsData, isLoading] = usePersistedData(leaseDealsDataProp);
  const [viewMode, setViewMode] = useState('grid');
  const listRef = useRef(null);
  const location = useSelector(state => state.pageContext?.location);
  const isShop4931ZeroDownRoute = getQuery(location)?.[ZERO_DOWN_PARAM] === 'true';

  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);
  }, []);

  const userSelectedStateName = visitorLocation?.state;

  useRedirectOnStateChange(urlContext, userSelectedStateName);
  useRestoreFiltersFromHistory(setModelValue);

  const deals = leaseDealsData?.deals || [];
  const publishDate = deals[0]?.publishDate;

  return (
    <main id="main-content" className="bg-cool-gray-80 pb-1">
      <Loading className="pos-f top-0 left-0 right-0 z-3" />

      <LeaseDealsGradientContainer isMobile={isMobile} withPreviewCard={withPreviewCard}>
        <LeaseDealsHeader isMobile={isMobile} publishDate={publishDate} withPreviewCard={withPreviewCard} />

        <Experiment name="shop-4931-icon-nav" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chal">
            <LeaseDealsIconNav isMobile={isMobile} withPreviewCard={withPreviewCard} />
          </Recipe>
        </Experiment>

        {withPreviewCard && <LeaseDealPreview />}

        <Container className="lease-deals-filters-container d-flex justify-content-between align-items-center mb-1_5 px-1_5 px-md-0_75 mt-2 mt-md-1 row-gap-0">
          <LeaseDealsFilters />
          <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
          <LeaseDealsSortToggle />
        </Container>
      </LeaseDealsGradientContainer>

      <Container>
        {!!deals?.length && (
          <LeaseDealsCardList
            isMobile={isMobile}
            deals={deals}
            type={viewMode}
            dealsCount={leaseDealsDataProp?.topDealsByModelCount}
            setModelValue={setModelValue}
            containerRef={listRef}
            isShop4931ZeroDownRoute={isShop4931ZeroDownRoute}
          />
        )}
        {!deals?.length && !isLoading && <LowInventoryScreen dealsCount={leaseDealsDataProp?.topDealsByModelCount} />}

        <LeaseDealsEvaluation />
        <DealCheckerWidget className="mt-3" />
        <LeaseDealsDisclaimer />
        <LeaseDealsTypeContent leaseDealsData={leaseDealsData} />
      </Container>

      <Shop3947Experiment />
      <TrackInvalidDealId />
    </main>
  );
}

LeaseDealsUI.propTypes = {
  urlContext: PropTypes.shape({
    location: PropTypes.shape({
      stateName: PropTypes.string,
    }),
  }),
  visitorLocation: PropTypes.shape({
    state: PropTypes.string,
  }),
  leaseDealsData: LeaseDealsEntities.LeaseDealsData,
  withPreviewCard: PropTypes.bool,
  isMobile: PropTypes.bool,
  setModelValue: PropTypes.func.isRequired,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

const stateToPropsConfig = {
  urlContext: bindToPath(URL_CONTEXT_PATH, UrlModel, null, false),
  visitorLocation: bindToPath('location', VisitorModel, null, false),
  leaseDealsData: bindToPath(LeaseDealsPaths.getLeaseDealsPath(), LeaseDealsModel, null, false),
};

export const LeaseDeals = flow(
  component => connectToModel(component, stateToPropsConfig),
  connect(mapStateToProps),
  withLeaseDealsListTracking
)(LeaseDealsUI);
